/* eslint-disable max-lines */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Upload,
  AlertCircle,
  CheckCircle2,
  X,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowLeft,
  Trash2
} from 'lucide-react';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useBulkCreateSpeakers } from '@utils/api/organizations/speakers';
import {
  BulkCreateSpeakerError,
  CreateSpeakerPayload,
  Speaker,
  SpeakerListResponse
} from '@utils/types/organization/speaker.ts';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import {
  BULK_MAX_ROWS,
  ParsedRow,
  RowIssue,
  downloadXlsxTemplate,
  parseCsvFile,
  parseTextInput,
  parseXlsxFile,
  validateRow
} from './bulk-upload-parsers';

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type InputMode = 'file' | 'paste';
type Step = 'input' | 'review';

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'];

export default function BulkUploadModal({
  isOpen,
  onClose
}: BulkUploadModalProps) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const isDark = theme === 'dark';

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('input');
  const [inputMode, setInputMode] = useState<InputMode>('file');
  const [textInput, setTextInput] = useState('');
  const [fileName, setFileName] = useState<string>('');
  const [fileRows, setFileRows] = useState<ParsedRow[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Populated on entering review step from the /bulk?dry_run=true call.
  const [dryRunConflicts, setDryRunConflicts] = useState<Map<number, string>>(
    new Map()
  );
  // Name-only conflicts: parsedSpeaker index → matching existing directory speaker.
  // These are soft warnings — user decides per-row whether to create anyway or skip.
  const [nameConflicts, setNameConflicts] = useState<Map<number, Speaker>>(
    new Map()
  );
  const [nameConflictDecisions, setNameConflictDecisions] = useState<
    Map<number, 'create' | 'skip'>
  >(new Map());
  const [uploadResult, setUploadResult] = useState<{
    success: number;
    failed: number;
    errors: BulkCreateSpeakerError[];
  } | null>(null);

  const bulkCreateMutation = useBulkCreateSpeakers();
  const dryRunMutation = useBulkCreateSpeakers();

  const parsedSpeakers = useMemo<ParsedRow[]>(() => {
    return inputMode === 'file' ? fileRows : parseTextInput(textInput);
  }, [inputMode, fileRows, textInput]);

  // A row is upload-eligible when it has no client-side error. Duplicate
  // warnings from the dry-run are surfaced but do NOT block upload — the
  // backend also rejects them per-row, so if the user confirms anyway they'll
  // get the same errors back with the actual /bulk call.
  const rowIssues = useMemo(() => {
    return parsedSpeakers.map(row => validateRow(row, t));
  }, [parsedSpeakers, t]);

  const validSpeakers = useMemo(() => {
    return parsedSpeakers.filter(
      (_, i) => !rowIssues[i].some(iss => iss.severity === 'error')
    );
  }, [parsedSpeakers, rowIssues]);

  const invalidCount = parsedSpeakers.length - validSpeakers.length;

  useEffect(() => {
    if (!isOpen) return;
    if (step === 'input') {
      setDryRunConflicts(new Map());
      setNameConflicts(new Map());
      setNameConflictDecisions(new Map());
    }
  }, [isOpen, step]);

  const handleClose = () => {
    setStep('input');
    setInputMode('file');
    setTextInput('');
    setFileName('');
    setFileRows([]);
    setDryRunConflicts(new Map());
    setNameConflicts(new Map());
    setNameConflictDecisions(new Map());
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const handleFile = async (file: File) => {
    const lower = file.name.toLowerCase();
    const isCsv = lower.endsWith('.csv');
    const isXlsx = lower.endsWith('.xlsx');
    if (!isCsv && !isXlsx) {
      toast.error(t('bulk-upload-unsupported-file-type'));
      return;
    }
    setIsParsing(true);
    try {
      const rows = isCsv ? await parseCsvFile(file) : await parseXlsxFile(file);
      if (rows.length === 0) {
        toast.error(t('bulk-upload-file-empty'));
        return;
      }
      if (rows.length > BULK_MAX_ROWS) {
        toast.error(t('bulk-upload-too-many-rows', { max: BULK_MAX_ROWS }));
      }
      setFileRows(rows.slice(0, BULK_MAX_ROWS));
      setFileName(file.name);
      setInputMode('file');
    } catch (err) {
      // Library-internal messages (e.g. read-excel-file's "Couldn't read
      // 'inline string' cell value" when a generator emits malformed cells)
      // are opaque to users. Fall back to the friendly copy for XLSX; keep
      // the raw message for CSV where Papa's errors are usually actionable.
      const raw = err instanceof Error ? err.message : '';
      toast.error(
        isXlsx || !raw
          ? t('bulk-upload-xlsx-parse-error')
          : raw || t('bulk-upload-parse-error')
      );
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    // Allow re-selecting the same file after clearing.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const handleClearFile = () => {
    setFileRows([]);
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Backend types role/party as `str` with default "" — sending null 422s.
  // email/notes are `str | None` so null is fine there.
  const toPayload = (rows: ParsedRow[]): CreateSpeakerPayload[] =>
    rows.map(s => ({
      name: s.name,
      role: s.role || '',
      party: s.party || '',
      email: s.email || null,
      notes: s.notes || null
    }));

  const handleReview = async () => {
    if (validSpeakers.length === 0) {
      toast.error(t('no-valid-speakers-to-upload'));
      return;
    }
    if (validSpeakers.length > BULK_MAX_ROWS) {
      toast.error(t('bulk-upload-too-many-rows', { max: BULK_MAX_ROWS }));
      return;
    }
    // Dry-run: ask backend which valid rows would collide, so the review
    // step can surface those *before* the user commits.
    try {
      const result = await dryRunMutation.mutateAsync({
        speakers: toPayload(validSpeakers),
        dryRun: true
      });
      // Map error index (position within validSpeakers) back to the row's
      // index in parsedSpeakers so the review table can highlight it.
      const conflicts = new Map<number, string>();
      const validIndexToParsedIndex = new Map<number, number>();
      let vi = 0;
      parsedSpeakers.forEach((_, pi) => {
        if (!rowIssues[pi].some(iss => iss.severity === 'error')) {
          validIndexToParsedIndex.set(vi, pi);
          vi += 1;
        }
      });
      for (const err of result.errors) {
        const parsedIdx = validIndexToParsedIndex.get(err.index);
        if (parsedIdx !== undefined) conflicts.set(parsedIdx, err.error);
      }
      setDryRunConflicts(conflicts);

      // Name-only conflict check: for each valid row not already flagged as an
      // exact match, search the directory for a speaker with the same name.
      // Groups by unique name to avoid redundant API calls.
      const namesToCheck = new Map<string, number[]>();
      parsedSpeakers.forEach((row, pi) => {
        if (rowIssues[pi].some(iss => iss.severity === 'error')) return;
        if (conflicts.has(pi)) return; // already an exact-match conflict
        const lower = row.name.trim().toLowerCase();
        if (!namesToCheck.has(lower)) namesToCheck.set(lower, []);
        namesToCheck.get(lower)!.push(pi);
      });

      const nameMatches = new Map<number, Speaker>();
      await Promise.all(
        Array.from(namesToCheck.entries()).map(
          async ([lowerName, parsedIdxs]) => {
            try {
              const qs = new URLSearchParams();
              qs.set('offset', '0');
              qs.set('limit', '10');
              qs.append('q', `name:ilike:%${lowerName}%`);
              const url = `${GET_URL().speakers}?${qs.toString()}`;
              const { data: res } = await Axios.get<SpeakerListResponse>(url);
              const match = res.result?.find(
                s => s.name.trim().toLowerCase() === lowerName
              );
              if (match) parsedIdxs.forEach(pi => nameMatches.set(pi, match));
            } catch {
              // best-effort: missing a near-duplicate warning is acceptable
            }
          }
        )
      );
      setNameConflicts(nameMatches);
      const decisions = new Map<number, 'create' | 'skip'>();
      nameMatches.forEach((_, pi) => decisions.set(pi, 'create'));
      setNameConflictDecisions(decisions);
    } catch {
      // Backend errored on the pre-check — don't block; the user can still
      // confirm, and the real /bulk call will surface the same errors.
      setDryRunConflicts(new Map());
    }
    setStep('review');
  };

  const handleConfirmUpload = async () => {
    // Build valid→parsed index map to respect per-row skip decisions.
    const validToParsed = new Map<number, number>();
    let vi = 0;
    parsedSpeakers.forEach((_, pi) => {
      if (!rowIssues[pi].some(iss => iss.severity === 'error')) {
        validToParsed.set(vi, pi);
        vi++;
      }
    });
    const speakersToUpload = validSpeakers.filter((_, vIdx) => {
      const pi = validToParsed.get(vIdx);
      return pi === undefined || nameConflictDecisions.get(pi) !== 'skip';
    });

    if (speakersToUpload.length === 0) {
      toast.error(t('no-valid-speakers-to-upload'));
      return;
    }

    try {
      const result = await bulkCreateMutation.mutateAsync({
        speakers: toPayload(speakersToUpload)
      });

      setUploadResult({
        success: result.count,
        failed: result.errors.length,
        errors: result.errors
      });

      if (result.count > 0 && result.errors.length === 0) {
        toast.success(
          t('speakers-uploaded-successfully', { count: result.count })
        );
        handleClose();
      } else if (result.count > 0) {
        toast.success(
          t('speakers-partially-uploaded', {
            success: result.count,
            failed: result.errors.length
          })
        );
      } else {
        toast.error(t('failed-to-upload-speakers'));
      }
    } catch {
      toast.error(t('failed-to-upload-speakers'));
    }
  };

  const isUploading = bulkCreateMutation.isLoading;
  const isPreflighting = dryRunMutation.isLoading || isParsing;

  const footerActions =
    step === 'input'
      ? [
          <Button
            key="cancel"
            variant="secondary"
            onClick={handleClose}
            label={t('cancel')}
            disabled={isPreflighting}
          />,
          <Button
            key="next"
            onClick={handleReview}
            label={t('review-and-continue')}
            loading={isPreflighting}
            disabled={isPreflighting || validSpeakers.length === 0}
          />
        ]
      : [
          <Button
            key="back"
            variant="secondary"
            onClick={() => setStep('input')}
            label={t('back')}
            icon={<ArrowLeft size={14} />}
            disabled={isUploading}
          />,
          <Button
            key="confirm"
            onClick={handleConfirmUpload}
            label={t('confirm-and-upload')}
            icon={<Upload size={14} />}
            loading={isUploading}
            disabled={isUploading || validSpeakers.length === 0}
          />
        ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        step === 'input'
          ? t('bulk-upload-speakers')
          : t('bulk-upload-review-title')
      }
      caption={
        step === 'input'
          ? t('bulk-upload-speakers-description')
          : t('bulk-upload-review-caption', {
              count: validSpeakers.length
            })
      }
      customWidth="720px"
      footerActions={footerActions}
    >
      {step === 'input' ? (
        <div className="space-y-4">
          {/* Input mode toggle + template download */}
          <div className="flex items-center justify-between gap-2">
            <div className="inline-flex rounded-2xl border border-hairline overflow-hidden">
              <button
                type="button"
                onClick={() => setInputMode('file')}
                className={`px-3 py-1.5 text-sm flex items-center gap-2${inputMode === 'file' ? ' bg-app-soft' : ''}`}
              >
                <FileSpreadsheet size={14} />
                {t('bulk-upload-file-tab')}
              </button>
              <button
                type="button"
                onClick={() => setInputMode('paste')}
                className={
                  'px-3 py-1.5 text-sm flex items-center gap-2 border-l border-hairline' +
                  (inputMode === 'paste' ? ' bg-app-soft' : '')
                }
              >
                <FileText size={14} />
                {t('bulk-upload-paste-tab')}
              </button>
            </div>
            <button
              type="button"
              onClick={() => void downloadXlsxTemplate(t)}
              className="flex items-center gap-1 underline-offset-2 hover:underline"
              style={{ color: getColour('activeText') }}
            >
              <Download size={14} />
              <Text as="span" size="sm">
                {t('download-template')}
              </Text>
            </button>
          </div>

          {inputMode === 'file' ? (
            <>
              {/* Dropzone */}
              <div
                onDragOver={e => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed rounded-2xl px-4 py-8 text-center cursor-pointer transition-colors"
                style={{
                  borderColor: isDragging
                    ? getColour('activeText')
                    : getColour('hairline'),
                  backgroundColor: isDragging
                    ? getColour('appSoft')
                    : 'transparent'
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS.join(',')}
                  onChange={handleFileInput}
                  className="hidden"
                />
                {fileName ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileSpreadsheet
                      size={16}
                      style={{ color: getColour('activeText') }}
                    />
                    <Text as="span" size="sm">
                      {fileName}
                    </Text>
                    <Text as="span" size="sm" tone="muted">
                      · {fileRows.length} {t('rows')}
                    </Text>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        handleClearFile();
                      }}
                      className="ml-2 p-1 rounded hover:opacity-70"
                      aria-label={t('remove-file')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Upload size={22} className="mx-auto" />
                    <Text as="div" size="sm">
                      {t('bulk-upload-dropzone-title')}
                    </Text>
                    <Text as="div" size="xs" tone="muted">
                      {t('bulk-upload-dropzone-hint')}
                    </Text>
                  </div>
                )}
              </div>
              <Text as="p" size="xs" tone="muted">
                {t('bulk-upload-columns-hint')}
              </Text>
            </>
          ) : (
            <>
              <div className="space-y-1">
                <Text as="p" size="sm">
                  {t('bulk-upload-instructions')}
                </Text>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>
                    <Text as="span" size="xs" tone="muted">
                      {t('bulk-upload-format-simple')}
                    </Text>
                  </li>
                  <li>
                    <Text as="span" size="xs" tone="muted">
                      {t('bulk-upload-format-csv')}
                    </Text>
                  </li>
                  <li>
                    <Text as="span" size="xs" tone="muted">
                      {t('bulk-upload-format-pipe')}
                    </Text>
                  </li>
                </ul>
              </div>
              <textarea
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
                placeholder={t('bulk-upload-placeholder')}
                className="w-full h-48 px-3 py-2 border border-hairline bg-app-soft rounded-2xl text-sm font-mono focus:outline-none focus:ring-2 resize-none"
                style={{
                  ['--tw-ring-color' as never]: getColour('activeText')
                }}
                disabled={isPreflighting}
              />
            </>
          )}

          {/* Live count summary (both modes) */}
          {parsedSpeakers.length > 0 && (
            <Text
              as="div"
              size="xs"
              tone="muted"
              className="flex items-center gap-3"
            >
              <span
                className="flex items-center gap-1"
                style={{
                  color: isDark ? 'rgb(74,222,128)' : 'rgb(22,163,74)'
                }}
              >
                <CheckCircle2 size={12} />
                {validSpeakers.length} {t('valid')}
              </span>
              {invalidCount > 0 && (
                <span
                  className="flex items-center gap-1"
                  style={{
                    color: isDark ? 'rgb(251,191,36)' : 'rgb(217,119,6)'
                  }}
                >
                  <AlertCircle size={12} />
                  {invalidCount} {t('invalid')}
                </span>
              )}
            </Text>
          )}
        </div>
      ) : (
        // ---- Review step ----
        <div className="space-y-3">
          <div
            className="rounded-2xl border p-3 text-sm flex items-start gap-2"
            style={{
              borderColor: isDark
                ? 'rgba(251,191,36,0.4)'
                : 'rgba(217,119,6,0.4)',
              backgroundColor: isDark
                ? 'rgba(251,191,36,0.08)'
                : 'rgba(217,119,6,0.06)',
              color: isDark ? 'rgb(253,224,71)' : 'rgb(146,64,14)'
            }}
          >
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">
                {t('bulk-upload-review-verify-title')}
              </div>
              <div className="mt-1">
                {t('bulk-upload-review-verify-body', {
                  count: validSpeakers.length
                })}
              </div>
              {dryRunConflicts.size > 0 && (
                <div className="mt-1">
                  {t('bulk-upload-review-conflicts', {
                    count: dryRunConflicts.size
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="border border-hairline rounded-2xl overflow-hidden">
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-app-soft">
                  <tr>
                    {['#', 'name', 'role', 'party', 'email', 'status'].map(
                      h => (
                        <th
                          key={h}
                          className="text-left px-3 py-1.5"
                          style={
                            h === 'status' ? { minWidth: '200px' } : undefined
                          }
                        >
                          <Text
                            as="span"
                            size="xs"
                            weight="medium"
                            tone="muted"
                          >
                            {h === '#' ? '#' : t(h)}
                          </Text>
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {parsedSpeakers.map((row, idx) => {
                    const issues = rowIssues[idx];
                    const conflict = dryRunConflicts.get(idx);
                    const nameConflict = nameConflicts.get(idx);
                    const decision = nameConflictDecisions.get(idx);
                    const isSkipped = decision === 'skip';
                    const hasError = issues.some(i => i.severity === 'error');
                    const hasExactConflict = !hasError && !!conflict;
                    const hasNameConflict =
                      !hasError && !hasExactConflict && !!nameConflict;
                    return (
                      <tr
                        key={idx}
                        className="border-t border-hairline"
                        style={{
                          opacity: isSkipped ? 0.45 : 1,
                          backgroundColor: hasError
                            ? isDark
                              ? 'rgba(239,68,68,0.12)'
                              : 'rgba(239,68,68,0.06)'
                            : hasExactConflict
                              ? isDark
                                ? 'rgba(251,191,36,0.10)'
                                : 'rgba(217,119,6,0.06)'
                              : hasNameConflict
                                ? isDark
                                  ? 'rgba(96,165,250,0.08)'
                                  : 'rgba(37,132,245,0.05)'
                                : 'transparent'
                        }}
                      >
                        <td className="px-3 py-1.5">
                          <Text as="span" size="xs" tone="muted">
                            {row.lineNumber}
                          </Text>
                        </td>
                        <RowCell
                          value={row.name}
                          hasError={hasError}
                          isDark={isDark}
                        />
                        <RowCell
                          value={row.role}
                          hasError={hasError}
                          isDark={isDark}
                          muted
                        />
                        <RowCell
                          value={row.party}
                          hasError={hasError}
                          isDark={isDark}
                          muted
                        />
                        <RowCell
                          value={row.email}
                          hasError={hasError}
                          isDark={isDark}
                          muted
                        />
                        <td className="px-3 py-1.5">
                          <RowStatus
                            issues={issues}
                            conflict={conflict}
                            nameConflict={nameConflict}
                            decision={decision}
                            onDecisionChange={d =>
                              setNameConflictDecisions(prev => {
                                const next = new Map(prev);
                                next.set(idx, d);
                                return next;
                              })
                            }
                            isDark={isDark}
                            t={t}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {invalidCount > 0 && (
            <p
              className="text-xs"
              style={{
                color: isDark ? 'rgb(252,165,165)' : 'rgb(185,28,28)'
              }}
            >
              {t('bulk-upload-review-invalid-skipped', {
                count: invalidCount
              })}
            </p>
          )}

          {uploadResult && (
            <div className="border border-hairline rounded-2xl p-3 space-y-2">
              <div className="flex items-center gap-4">
                {uploadResult.success > 0 && (
                  <span
                    className="flex items-center gap-1 text-sm"
                    style={{
                      color: isDark ? 'rgb(74,222,128)' : 'rgb(22,163,74)'
                    }}
                  >
                    <CheckCircle2 size={14} />
                    {uploadResult.success} {t('uploaded')}
                  </span>
                )}
                {uploadResult.failed > 0 && (
                  <span
                    className="flex items-center gap-1 text-sm"
                    style={{
                      color: isDark ? 'rgb(248,113,113)' : 'rgb(220,38,38)'
                    }}
                  >
                    <X size={14} />
                    {uploadResult.failed} {t('failed')}
                  </span>
                )}
              </div>
              {uploadResult.errors.length > 0 && (
                <div
                  className="text-xs space-y-1 max-h-24 overflow-y-auto"
                  style={{
                    color: isDark ? 'rgb(248,113,113)' : 'rgb(220,38,38)'
                  }}
                >
                  {uploadResult.errors.map((err, idx) => (
                    <div key={idx}>
                      <strong>{err.name}:</strong> {err.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

interface RowCellProps {
  value?: string;
  hasError: boolean;
  muted?: boolean;
  isDark: boolean;
}

function RowCell({ value, hasError, muted, isDark }: RowCellProps) {
  if (hasError) {
    return (
      <td
        className="px-3 py-1.5 text-xs"
        style={{ color: isDark ? 'rgb(252,165,165)' : 'rgb(185,28,28)' }}
      >
        {value || '-'}
      </td>
    );
  }
  return (
    <td className="px-3 py-1.5">
      <Text as="span" size="xs" tone={muted ? 'muted' : undefined}>
        {value || '-'}
      </Text>
    </td>
  );
}

interface RowStatusProps {
  issues: RowIssue[];
  conflict?: string;
  nameConflict?: Speaker;
  decision?: 'create' | 'skip';
  onDecisionChange?: (d: 'create' | 'skip') => void;
  isDark: boolean;
  t: (k: string, vars?: Record<string, string>) => string;
}

function RowStatus({
  issues,
  conflict,
  nameConflict,
  decision,
  onDecisionChange,
  isDark,
  t
}: RowStatusProps) {
  const err = issues.find(i => i.severity === 'error');
  if (err) {
    return (
      <span
        className="flex items-center gap-1"
        style={{ color: isDark ? 'rgb(252,165,165)' : 'rgb(185,28,28)' }}
      >
        <AlertCircle size={12} />
        {err.message}
      </span>
    );
  }
  if (conflict) {
    return (
      <span
        className="flex items-center gap-1"
        style={{ color: isDark ? 'rgb(251,191,36)' : 'rgb(217,119,6)' }}
      >
        <AlertCircle size={12} />
        {conflict}
      </span>
    );
  }
  if (nameConflict) {
    if (decision === 'skip') {
      return (
        <span className="flex items-center gap-1.5 flex-wrap">
          <span
            className="flex items-center gap-1"
            style={{ color: isDark ? 'rgb(148,163,184)' : 'rgb(100,116,139)' }}
          >
            <X size={12} />
            {t('will-be-skipped')}
          </span>
          <button
            type="button"
            className="underline underline-offset-2 text-xs"
            style={{ color: isDark ? 'rgb(96,165,250)' : 'rgb(37,99,235)' }}
            onClick={() => onDecisionChange?.('create')}
          >
            {t('undo')}
          </button>
        </span>
      );
    }
    return (
      <span className="flex flex-col gap-1">
        <span
          className="flex items-center gap-1"
          style={{ color: isDark ? 'rgb(96,165,250)' : 'rgb(37,99,235)' }}
        >
          <AlertCircle size={12} />
          {t('bulk-upload-name-exists', { name: nameConflict.name })}
        </span>
        <span className="flex items-center gap-1.5">
          <button
            type="button"
            className="px-1.5 py-0.5 rounded text-xs border"
            style={{
              borderColor: isDark
                ? 'rgba(96,165,250,0.4)'
                : 'rgba(37,99,235,0.3)',
              color: isDark ? 'rgb(96,165,250)' : 'rgb(37,99,235)'
            }}
            onClick={() => onDecisionChange?.('create')}
          >
            {t('create-anyway')}
          </button>
          <button
            type="button"
            className="px-1.5 py-0.5 rounded text-xs border"
            style={{
              borderColor: isDark
                ? 'rgba(148,163,184,0.3)'
                : 'rgba(100,116,139,0.3)',
              color: isDark ? 'rgb(148,163,184)' : 'rgb(100,116,139)'
            }}
            onClick={() => onDecisionChange?.('skip')}
          >
            {t('skip-row')}
          </button>
        </span>
      </span>
    );
  }
  return (
    <span
      className="flex items-center gap-1"
      style={{ color: isDark ? 'rgb(74,222,128)' : 'rgb(22,163,74)' }}
    >
      <CheckCircle2 size={12} />
      {t('ready')}
    </span>
  );
}
